import React, { useMemo } from 'react';
import { Truck } from 'lucide-react';
import type { CarrierServiceOption } from '@/types/paymentRouting';
import { groupCarrierServices, toggleCarrierBlock } from './weightedRoutingUtils';

type Block = { provider_id: number; provider_service_id: number | null };

interface CarrierBlocksEditorProps {
  options: CarrierServiceOption[];
  blocks: Block[];
  onChange: (blocks: Block[]) => void;
  disabled?: boolean;
  /** Erro ao carregar a lista de transportadoras/serviços. */
  loadError?: string | null;
}

/**
 * Checklist "Transportadoras bloqueadas" (transportadora REAL + serviço, chaves de
 * [Shipping].[Providers] / [Shipping].[ProvidersService]). Marcar "todos os serviços" bloqueia a
 * transportadora inteira. Ex.: Viver Organizer Ltda bloqueia "MelhorEnvio - LATAM Cargo · éFácil"
 * (o "Expresso Aereo" do site), porque a conta do Melhor Envio só atende a Store.
 */
export const CarrierBlocksEditor: React.FC<CarrierBlocksEditorProps> = ({
  options, blocks, onChange, disabled, loadError,
}) => {
  const groups = useMemo(() => groupCarrierServices(options), [options]);

  const isChecked = (providerId: number, serviceId: number | null) =>
    blocks.some(b => b.provider_id === providerId && b.provider_service_id === serviceId);

  return (
    <div>
      <label className="flex items-center gap-1.5 text-sm font-medium text-slate-700 mb-1">
        <Truck size={14} className="text-slate-500" />
        Transportadoras bloqueadas
      </label>
      {loadError && <p className="text-xs text-red-600 mb-1">{loadError}</p>}
      {groups.length === 0 && !loadError && (
        <p className="text-xs text-slate-500">Nenhuma transportadora cadastrada.</p>
      )}
      <div className="space-y-2">
        {groups.map(group => {
          const allBlocked = isChecked(group.provider_id, null);
          return (
            <div key={group.provider_id} className="rounded-lg border border-slate-200 p-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-medium text-slate-700">{group.provider_name}</span>
                <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 rounded border-slate-300 text-red-600 focus:ring-red-500"
                    checked={allBlocked}
                    disabled={disabled}
                    onChange={() => onChange(toggleCarrierBlock(blocks, group.provider_id, null))}
                  />
                  Todos os serviços
                </label>
              </div>
              <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-1">
                {group.services.map(svc => {
                  const checked = allBlocked || isChecked(svc.provider_id, svc.provider_service_id);
                  return (
                    <label
                      key={svc.provider_service_id}
                      className={`flex items-start gap-2 text-xs rounded px-1.5 py-1 ${
                        checked ? 'bg-red-50 text-red-800' : 'text-slate-700'
                      } ${allBlocked || disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <input
                        type="checkbox"
                        className="mt-0.5 h-3.5 w-3.5 rounded border-slate-300 text-red-600 focus:ring-red-500"
                        checked={checked}
                        disabled={allBlocked || disabled}
                        onChange={() => onChange(toggleCarrierBlock(blocks, svc.provider_id, svc.provider_service_id))}
                      />
                      <span>
                        <span className="font-medium">{svc.provider_service_name}</span>
                        {svc.customer_label && (
                          <span className="text-slate-500"> — exibido como “{svc.customer_label}”</span>
                        )}
                        {svc.quotes_90d != null && svc.quotes_90d > 0 && (
                          <span className="text-slate-400"> · {svc.quotes_90d} cotações em 90 dias</span>
                        )}
                        {svc.note && <span className="block text-slate-500 italic">{svc.note}</span>}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
